import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ApiResponse } from "models";
import { groupAction } from "./groupSlice";
import groupApi from "api/group";

// ================== Get Group List ==================
function* getGroupList(action: PayloadAction<any>): any {
  try {
    const response: ApiResponse = yield call(groupApi.getRegionWiseBrand, {
      search: action.payload?.search || "",
    });
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(groupAction.getGroupListSuccess(data));
    } else {
      yield put(groupAction.getGroupListFailed(data));
      toast.error(data.message || "Failed to fetch group list");
    }
  } catch (error: any) {
    const errorData = error.response?.data || { message: "Something went wrong" };
    yield put(groupAction.getGroupListFailed(errorData));
    toast.error(errorData?.message || "Something went wrong");
  }
}

// ================== Get Device List ==================
function* getDeviceList(action: PayloadAction<any>) {
  try {
    const response: ApiResponse = yield call(groupApi.getDevices, action.payload);
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(groupAction.getDeviceListSuccess(data));
    } else {
      yield put(groupAction.getDeviceListFailed(data));
      toast.error(data.message || "Failed to fetch device list");
    }
  } catch (error: any) {
    const errorData = error.response?.data || { message: "Something went wrong" };
    yield put(groupAction.getDeviceListFailed(errorData));
    toast.error(errorData?.message || "Something went wrong");
  }
}

// ================== Add Device ==================
function* addDevice(action: PayloadAction<any>) {
  try {
    const response: ApiResponse = yield call(groupApi.add, action.payload);
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(groupAction.addDeviceSuccess(data));
      toast.success(data.message || "Device added successfully!");
      // ✅ optionally refresh list
      yield put(groupAction.getDeviceList({}));
    } else {
      yield put(groupAction.addDeviceFailed(data));
      toast.error(data.message || "Failed to add device");
    }
  } catch (error: any) {
    const errorData = error.response?.data || { message: "Something went wrong" };
    yield put(groupAction.addDeviceFailed(errorData));
    toast.error(errorData?.message || "Something went wrong");
  }
}

// ================== Update Device ==================
function* updateDevice(action: PayloadAction<any>) {
  try {
    const response: ApiResponse = yield call(groupApi.updateDevice, action.payload);
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(groupAction.updateDeviceSuccess(data));
      toast.success(data.message || "Device updated successfully!");
      // ✅ optionally refresh list
      yield put(groupAction.getDeviceList({}));
    } else {
      yield put(groupAction.updateDeviceFailed(data));
      toast.error(data.message || "Failed to update device");
    }
  } catch (error: any) {
    const errorData = error.response?.data || { message: "Something went wrong" };
    yield put(groupAction.updateDeviceFailed(errorData));
    toast.error(errorData?.message || "Something went wrong");
  }
}

// ================== Get Device By ID ==================
function* getDeviceById(action: PayloadAction<string>) {
  try {
    const response: ApiResponse = yield call(groupApi.getDeviceById, action.payload);
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(groupAction.getDeviceByIdSuccess(data));
    } else {
      yield put(groupAction.getDeviceByIdFailed(data));
      toast.error(data.message || "Failed to fetch device details");
    }
  } catch (error: any) {
    const errorData = error.response?.data || { message: "Something went wrong" };
    yield put(groupAction.getDeviceByIdFailed(errorData));
    toast.error(errorData?.message || "Something went wrong");
  }
}

// ================== Delete Device ==================
function* deleteDevice(action: PayloadAction<any>) {
  try {
    const response: ApiResponse = yield call(groupApi.deleteDevice, action.payload);
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(groupAction.deleteDeviceSuccess(data));
      toast.success(data.message || "Device deleted successfully!");
      // ✅ optionally refresh list
      yield put(groupAction.getDeviceList({}));
    } else {
      yield put(groupAction.deleteDeviceFailed(data));
      toast.error(data.message || "Failed to delete device");
    }
  } catch (error: any) {
    const errorData = error.response?.data || { message: "Something went wrong" };
    yield put(groupAction.deleteDeviceFailed(errorData));
    toast.error(errorData?.message || "Something went wrong");
  }
}

// ================== Root Saga ==================
export default function* groupSaga() {
  yield takeLatest(groupAction.getGroupList.type, getGroupList);
  yield takeLatest(groupAction.getDeviceList.type, getDeviceList);
  yield takeLatest(groupAction.addDevice.type, addDevice);
  yield takeLatest(groupAction.updateDevice.type, updateDevice);
  yield takeLatest(groupAction.getDeviceById.type, getDeviceById);
  yield takeLatest(groupAction.deleteDevice.type, deleteDevice);
}
